import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as UsersIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

import { useInstitution } from '../../context/InstitutionContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseconf';

// Recharts components import
import {
  LineChart,
  BarChart,
  PieChart,
  Line,
  Bar,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

export default function AnalyticsDashboard() {
  const { currentInstitution } = useInstitution();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalStudents: 0,
    totalClasses: 0,
    totalAssignments: 0,
    totalAttendance: 0,
    averageAttendance: 0
  });
  const [chartData, setChartData] = useState({
    userGrowth: [],
    attendanceByMonth: [],
    usersByRole: [],
    marksDistribution: []
  });

  useEffect(() => {
    if (currentInstitution?.id) {
      fetchAnalytics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInstitution?.id]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      // Fetch users count
      const usersRef = collection(db, 'institutions', currentInstitution.id, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const users = usersSnapshot.docs;
      const totalUsers = users.length;

      // Count by role
      const roleCount = {
        teacher: 0,
        admin: 0,
        principal: 0,
        staff: 0
      };
      users.forEach(doc => {
        const role = doc.data().role;
        if (roleCount[role] !== undefined) {
          roleCount[role]++;
        }
      });

      // Fetch students count
      const studentsRef = collection(db, 'institutions', currentInstitution.id, 'students');
      const studentsSnapshot = await getDocs(studentsRef);
      const totalStudents = studentsSnapshot.docs.length;

      // Fetch classes count
      const classesRef = collection(db, 'institutions', currentInstitution.id, 'classes');
      const classesSnapshot = await getDocs(classesRef);
      const totalClasses = classesSnapshot.docs.length;

      // Fetch assignments count
      const assignmentsRef = collection(db, 'institutions', currentInstitution.id, 'assignments');
      const assignmentsSnapshot = await getDocs(assignmentsRef);
      const totalAssignments = assignmentsSnapshot.docs.length;

      // Update stats
      setStats({
        totalUsers,
        totalStudents,
        totalClasses,
        totalAssignments,
        totalAttendance: Math.floor(totalStudents * 0.85), // Placeholder
        averageAttendance: 85 // Placeholder
      });

      // Prepare chart data
      const usersByRoleData = [
        { name: 'Teachers', value: roleCount.teacher, color: '#1976d2' },
        { name: 'Admin', value: roleCount.admin, color: '#f44336' },
        { name: 'Principal', value: roleCount.principal, color: '#4caf50' },
        { name: 'Staff', value: roleCount.staff, color: '#ff9800' }
      ].filter(item => item.value > 0);

      const userGrowthData = [
        { month: 'Jan', users: 10 },
        { month: 'Feb', users: 15 },
        { month: 'Mar', users: 22 },
        { month: 'Apr', users: 28 },
        { month: 'May', users: 35 },
        { month: 'Jun', users: totalUsers }
      ];

      const attendanceByMonthData = [
        { month: 'Jan', attendance: 88 },
        { month: 'Feb', attendance: 85 },
        { month: 'Mar', attendance: 90 },
        { month: 'Apr', attendance: 87 },
        { month: 'May', attendance: 92 },
        { month: 'Jun', attendance: 85 }
      ];

      const marksDistributionData = [
        { range: 'A (90-100)', count: 15 },
        { range: 'B (80-89)', count: 35 },
        { range: 'C (70-79)', count: 28 },
        { range: 'D (60-69)', count: 15 },
        { range: 'F (<60)', count: 7 }
      ];

      setChartData({
        userGrowth: userGrowthData,
        attendanceByMonth: attendanceByMonthData,
        usersByRole: usersByRoleData,
        marksDistribution: marksDistributionData
      });

      setError('');
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <h2>Analytics Dashboard</h2>
        <p style={{ color: '#666' }}>Institution Performance & Statistics</p>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {stats.totalUsers}
                  </Typography>
                </Box>
                <UsersIcon sx={{ fontSize: 40, color: '#1976d2', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Students
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {stats.totalStudents}
                  </Typography>
                </Box>
                <SchoolIcon sx={{ fontSize: 40, color: '#4caf50', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Classes
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {stats.totalClasses}
                  </Typography>
                </Box>
                <AssignmentIcon sx={{ fontSize: 40, color: '#ff9800', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Attendance
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {stats.averageAttendance}%
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: '#f44336', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* User Growth Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>User Growth</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData.userGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="users" stroke="#1976d2" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Attendance Trend Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Monthly Attendance Trend</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.attendanceByMonth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="attendance" fill="#4caf50" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Users by Role */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Users by Role</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.usersByRole}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.usersByRole.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Marks Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Marks Distribution</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.marksDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#ff9800" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Summary Section */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Key Insights</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="body2">
                  📊 <strong>User Base:</strong> You have {stats.totalUsers} active users with {stats.totalStudents} students enrolled.
                </Typography>
              </Box>
              <Box sx={{ p: 2, backgroundColor: '#f3e5f5', borderRadius: 1 }}>
                <Typography variant="body2">
                  📈 <strong>Attendance:</strong> Average attendance rate is {stats.averageAttendance}%, indicating good student engagement.
                </Typography>
              </Box>
              <Box sx={{ p: 2, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
                <Typography variant="body2">
                  👥 <strong>Classes:</strong> {stats.totalClasses} classes are active. Consider optimizing class distribution for better management.
                </Typography>
              </Box>
              <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                <Typography variant="body2">
                  📝 <strong>Assignments:</strong> {stats.totalAssignments} assignments given out. Keep tracking to maintain academic progress.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
